import React, { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Alert,
  Box,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AttachmentIcon from '@mui/icons-material/Attachment';

// Component for reject dialog
export const RejectDialog = ({ open, onClose, onReject }) => {
  const { t } = useTranslation(['workflows', 'common']);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const validateForm = () => {
    if (!reason.trim()) {
      setError(t('workflows:error_rejection_reason', 'Please provide a reason for rejection'));
      return false;
    }
    return true;
  };

  const handleReject = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await onReject(reason);
      // Dialog will be closed by the parent component
    } catch (err) {
      setError(err.message || t('workflows:rejection_error', 'An error occurred during rejection'));
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('workflows:reject_step', 'Reject Step')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('workflows:rejection_prompt', 'Please provide a reason for rejecting this task.')}
        </DialogContentText>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          id="reason"
          label={t('workflows:rejection_reason', 'Rejection Reason')}
          placeholder={t('workflows:rejection_reason_placeholder', 'Explain why you are rejecting this step')}
          fullWidth
          multiline
          rows={4}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError(null);
          }}
          variant="outlined"
          sx={{ mt: 2 }}
          required
          error={!!error}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common:cancel', 'Cancel')}
        </Button>
        <Button 
          variant="contained" 
          color="error" 
          onClick={handleReject} 
          disabled={loading || !reason.trim()}
        >
          {loading ? <CircularProgress size={24} /> : t('workflows:reject', 'Reject')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Component for approve dialog
export const ApproveDialog = ({ open, onClose, onApprove }) => {
  const { t } = useTranslation(['workflows', 'common']);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setRemarks('');
      setError(null);
    }
  }, [open]);
  
  const handleApprove = async () => {
    setLoading(true);
    try {
      await onApprove(remarks);
      // The dialog will be closed by the parent component after success
    } catch (err) {
      setError(err.message || t('workflows:approval_error', 'An error occurred during task completion'));
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('workflows:complete_task', 'Complete Task')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('workflows:complete_task_prompt', 'Add your completion notes for this task.')}
        </DialogContentText>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          margin="dense"
          id="remarks"
          label={t('workflows:completion_notes', 'Completion Notes')}
          placeholder={t('workflows:completion_notes_placeholder', 'Add notes about what you did to complete this task')}
          fullWidth
          multiline
          rows={4}
          value={remarks}
          onChange={(e) => {
            setRemarks(e.target.value);
            if (error) setError(null);
          }}
          variant="outlined"
          sx={{ mt: 2 }}
          disabled={loading}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common:cancel', 'Cancel')}
        </Button>
        <Button 
          variant="contained" 
          color="success" 
          onClick={handleApprove} 
          disabled={loading || !remarks.trim()}
        >
          {loading ? <CircularProgress size={24} /> : t('workflows:complete', 'Complete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 